import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  FileClock,
  Loader2,
  Sparkles,
} from "lucide-react";
import type { DashboardBrandSummary } from "@app/shared";
import { api } from "../lib/api";
import { useBrands } from "../lib/brand";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Building2;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal/10 text-teal">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold leading-tight text-navy">{value}</p>
          <p className="truncate text-xs text-slate-500">{sub ?? label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const { setActiveKey } = useBrands();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ["dashboard"],
    queryFn: ({ signal }) => api.dashboard(signal),
    refetchInterval: 30_000,
  });
  const d = q.data;

  const openBrand = (key: string) => {
    setActiveKey(key);
    navigate("/drafts");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy">Dashboard</h1>
        <p className="text-sm text-slate-500">
          Ringkasan lintas brand. Klik "Buka" untuk masuk ke workspace sebuah brand.
        </p>
      </div>

      {q.isLoading || !d ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : (
        <>
          {/* Stat row */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard icon={Building2} label="Brand" value={d.brands.length} sub="brand aktif" />
            <StatCard
              icon={FileClock}
              label="Perlu review"
              value={d.pendingReview}
              sub="draft menunggu approve"
            />
            <StatCard
              icon={CalendarClock}
              label="Jadwal 7 hari"
              value={d.upcoming7d}
              sub="akan tayang"
            />
            <StatCard
              icon={Sparkles}
              label="Aktivitas AI"
              value={d.today.generations}
              sub="hari ini"
            />
          </div>

          {/* Alerts */}
          {d.alerts.length > 0 ? (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  Perlu perhatian ({d.alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {d.alerts.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 text-sm text-slate-700"
                  >
                    <span>{a.message}</span>
                    {a.brandKey ? (
                      <button
                        type="button"
                        onClick={() => openBrand(a.brandKey as string)}
                        className="shrink-0 text-xs font-medium text-teal hover:underline"
                      >
                        Buka →
                      </button>
                    ) : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}

          {/* Brand summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ringkasan brand</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {d.brands.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-500">
                  Belum ada brand.
                </p>
              ) : (
                d.brands.map((b) => <BrandRow key={b.key} brand={b} onOpen={openBrand} />)
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function BrandRow({
  brand,
  onOpen,
}: {
  brand: DashboardBrandSummary;
  onOpen: (key: string) => void;
}) {
  const c = brand.counts;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3">
      <span
        className="h-9 w-9 shrink-0 rounded-md ring-1 ring-slate-200"
        style={{ backgroundColor: brand.accentColor ?? "#33BEBA" }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-navy">{brand.displayName}</span>
          <span className="font-mono text-xs text-slate-400">{brand.key}</span>
        </div>
        <p className="mt-0.5 text-xs text-slate-500">
          {c.drafts} draft · {c.articles} artikel · {c.materials} materi ·{" "}
          {c.videos} video
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <Badge variant={brand.igConnected ? "green" : "secondary"}>IG</Badge>
        <Badge variant={brand.wpConnected ? "green" : "secondary"}>WP</Badge>
        <Badge variant={brand.hasLogin ? "teal" : "coral"}>
          {brand.hasLogin ? "login" : "no login"}
        </Badge>
      </div>
      <Button variant="outline" size="sm" onClick={() => onOpen(brand.key)}>
        Buka
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
